import React from 'react'
import Preloader from '../components/Preloader'
import Header from '../components/Header'
import ScrollReveal from '../components/ScrollReveal'
import Footer from '../components/Footer'
import { Helmet } from 'react-helmet-async'
import { MdOutlineDesignServices } from 'react-icons/md'
import { FaCode, FaMobileAlt, FaShoppingCart, FaRocket, FaChartLine } from 'react-icons/fa'

const portfolioProjects = [
    {
        id: 1,
        title: 'Vertical Elevators Engineering Limited',
        category: 'Engineering',
        description: 'Mobility solutions company specializing in the design, installation, and maintenance of elevators, lifts and escalators, committed to providing safe and efficient vertical transportation.',
        technologies: ['Web application', 'Responsive UI'],
        imageUrl: '/assets/veel_logo.jpg',
        link: 'https://verticalelevators.com'
    },
    {
        id: 2,
        title: 'Querencia Micro Credit',
        category: 'Finance',
        description: 'A microfinance platform providing small loans to underserved communities, built with a focus on security and scalability.',
        technologies: ['Web application', 'Secure portal'],
        imageUrl: '/assets/querenciamicrocredit.jpeg',
        link: 'https://querenciamc.com/'
    },
    {
        id: 3,
        title: 'VRIFY Africa',
        category: 'Virtual Reality and Virtual Tours',
        description: 'A virtual reality platform offering immersive virtual tours and experiences across Africa, designed to showcase the continent\'s rich culture and landscapes.',
        technologies: ['Web application', 'Immersive content'],
        imageUrl: '/assets/vrifyafrica.png',
        link: 'https://vrifyafrica.com/'
    },
    {
        id: 4,
        title: 'Flosam Institute',
        category: 'Education',
        description: 'A comprehensive school management system designed to streamline administrative tasks and enhance the learning experience for students and teachers.',
        technologies: ['Web application', 'Admin dashboard'],
        imageUrl: '/assets/flosam.jpg',
        link: 'https://flosaminstitute.org/'
    },
    {
        id: 5,
        title: 'Richaine Technologies',
        category: 'Information Technology',
        description: 'An IT solutions provider offering a range of services including software development, IT consulting, and managed services to help businesses leverage technology for growth.',
        technologies: ['Web application', 'Admin dashboard'],
        imageUrl: '/assets/rechainenavlogo.png',
        link: 'https://richainetechnologies.com/'
    }
]

const services = [
    {
        title: 'Custom Web Platforms',
        description: 'Scalable websites and dashboards that turn your ideas into conversion-focused digital products.',
        icon: <FaCode className="text-2xl text-yellow-400" />
    },
    {
        title: 'Mobile-First Experiences',
        description: 'Responsive interfaces designed for flawless browsing across desktops, tablets, and mobile devices.',
        icon: <FaMobileAlt className="text-2xl text-yellow-400" />
    },
    {
        title: 'E-Commerce & Lead Funnels',
        description: 'Trackable user journeys, polished storefronts, and high-converting landing experiences for your audience.',
        icon: <FaShoppingCart className="text-2xl text-yellow-400" />
    },
    {
        title: 'Growth-Driven Optimization',
        description: 'Web performance, analytics, and UX improvements that support long-term business growth.',
        icon: <FaChartLine className="text-2xl text-yellow-400" />
    }
]

const processSteps = [
    {
        title: 'Discover',
        description: 'We align on your goals, audience, and the customer journey before designing anything.'
    },
    {
        title: 'Design',
        description: 'Our team creates intuitive layouts, brand-aligned visuals, and clear calls to action.'
    },
    {
        title: 'Develop',
        description: 'We build fast, secure, and accessible web experiences with flexible, maintainable code.'
    },
    {
        title: 'Launch & Improve',
        description: 'We deploy, track performance, and refine your site so it keeps delivering results.'
    }
]

const stats = [
    { value: '10+', label: 'projects delivered' },
    { value: '100%', label: 'client satisfaction' },
    { value: '24/7', label: 'support availability' },
    { value: '3x', label: 'faster launches' }
]

const WebDevelopment = () => {
    return (
        <div>
            <Helmet>
                <title>Web Development | VRIFY Technologies</title>
                <meta
                    name="description"
                    content="Professional web development services including responsive design, e-commerce, mobile apps, and more from VRIFY Technologies."
                />
                <meta
                    name="keywords"
                    content="web development, web design, e-commerce, mobile app, responsive design, VRIFY Technologies"
                />
                <meta property="og:title" content="Web Development | VRIFY Technologies" />
                <meta property="og:description" content="Custom web development solutions for your business." />
            </Helmet>

            <div className="font-nanum-myeongjo min-h-screen bg-slate-950 text-slate-100 pt-16 md:pt-20 lg:pt-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.25),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.16),_transparent_22%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <section className="mx-auto max-w-7xl px-4 pb-10 pt-8 sm:px-6 lg:px-8 lg:pb-16">
                        <ScrollReveal>
                            <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12">
                                <div>
                                    <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">
                                        <MdOutlineDesignServices className="text-sm" />
                                        Web development
                                    </p>
                                    <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                        Modern websites built to convert, scale, and impress.
                                    </h1>
                                    <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                        From high-performing landing pages to enterprise-grade web platforms, VRIFY Technologies helps your business look sharp and work beautifully on every screen.
                                    </p>

                                    <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                                        <a
                                            href="/contactus"
                                            className="inline-flex items-center justify-center rounded-full bg-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                        >
                                            Start your project
                                        </a>
                                        <a
                                            href="#portfolio"
                                            className="inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-3 text-sm font-semibold text-white transition hover:border-yellow-300 hover:text-yellow-200"
                                        >
                                            Explore our work
                                        </a>
                                    </div>

                                    <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                        {stats.map((stat) => (
                                            <div
                                                key={stat.label}
                                                className="rounded-2xl border border-white/10 bg-white/5 px-4 py-4 backdrop-blur-sm"
                                            >
                                                <p className="text-xl font-bold text-white">{stat.value}</p>
                                                <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-300">{stat.label}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="rounded-[28px] border border-white/10 bg-slate-900/85 p-5 shadow-2xl shadow-black/30 backdrop-blur sm:p-6">
                                    <div className="rounded-[24px] bg-gradient-to-br from-yellow-400 via-amber-300 to-slate-100 p-[1px]">
                                        <div className="rounded-[23px] bg-slate-950/95 p-5 sm:p-6">
                                            <p className="text-xs uppercase tracking-[0.3em] text-yellow-300">What you get</p>
                                            <div className="mt-5 space-y-4">
                                                {services.map((service, index) => (
                                                    <div
                                                        key={service.title}
                                                        className="rounded-2xl border border-white/10 bg-white/5 p-4"
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div className="mt-0.5 rounded-xl bg-slate-900 p-2">
                                                                {service.icon}
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-white">{service.title}</p>
                                                                <p className="mt-1 text-sm leading-6 text-slate-300">{service.description}</p>
                                                            </div>
                                                        </div>
                                                        {index < services.length - 1 ? (
                                                            <div className="mt-4 h-px bg-white/10" />
                                                        ) : null}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="flex flex-col gap-3 text-center sm:text-left">
                                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">How we work</p>
                                <h2 className="text-2xl font-bold text-white sm:text-3xl">A clear process that keeps your project moving.</h2>
                                <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                                    Every engagement is built around clarity, speed, and a polished user experience that feels native across all devices.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                            {processSteps.map((step, index) => (
                                <ScrollReveal key={step.title}>
                                    <div className="rounded-3xl border border-white/10 bg-white/5 p-5 shadow-lg shadow-black/10">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs uppercase tracking-[0.3em] text-yellow-300">0{index + 1}</p>
                                            <FaRocket className="text-lg text-yellow-300" />
                                        </div>
                                        <h3 className="mt-4 text-lg font-semibold text-white">{step.title}</h3>
                                        <p className="mt-2 text-sm leading-7 text-slate-300">{step.description}</p>
                                    </div>
                                </ScrollReveal>
                            ))}
                        </div>
                    </section>

                    <section id="portfolio" className="mx-auto max-w-7xl px-4 pb-4 pt-8 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="flex flex-col gap-3 text-center sm:text-left">
                                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">Portfolio</p>
                                <h2 className="text-2xl font-bold text-white sm:text-3xl">Selected work that blends performance and design.</h2>
                                <p className="max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                                    We build clean, responsive web experiences that feel premium whether viewed on a phone, laptop, or large desktop display.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                            {portfolioProjects.map((project) => (
                                <ScrollReveal key={project.id}>
                                    <article className="flex h-full flex-col overflow-hidden rounded-[24px] border border-white/10 bg-slate-900/80 shadow-xl shadow-black/20 transition duration-300 hover:-translate-y-1 hover:border-yellow-300/80">
                                        <div className="relative h-48 overflow-hidden">
                                            <img
                                                src={project.imageUrl}
                                                alt={project.title}
                                                className="h-full w-full object-cover transition duration-500 hover:scale-105"
                                                loading="lazy"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/10 to-transparent" />
                                            <span className="absolute left-4 top-4 rounded-full bg-yellow-400 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-slate-950">
                                                {project.category}
                                            </span>
                                        </div>

                                        <div className="flex flex-1 flex-col p-5">
                                            <h3 className="text-lg font-semibold text-white">{project.title}</h3>
                                            <p className="mt-3 flex-1 text-sm leading-7 text-slate-300">{project.description}</p>
                                            <div className="mt-4 flex flex-wrap gap-2">
                                                {project.technologies.map((tech) => (
                                                    <span
                                                        key={tech}
                                                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-slate-200"
                                                    >
                                                        {tech}
                                                    </span>
                                                ))}
                                            </div>
                                            <a
                                                href={project.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-5 inline-flex items-center justify-center rounded-full bg-yellow-400 px-4 py-2 text-sm font-bold text-slate-950 transition hover:bg-yellow-300"
                                            >
                                                View project
                                            </a>
                                        </div>
                                    </article>
                                </ScrollReveal>
                            ))}
                        </div>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10">
                                <div className="max-w-2xl">
                                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-950">Ready to build</p>
                                    <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">
                                        Turn your next idea into a polished, high-performing web experience.
                                    </h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-900/80 sm:text-base">
                                        Whether you need a new site, a redesign, or a more effective online sales funnel, we can help you launch faster and look better on every device.
                                    </p>
                                </div>
                                <div className="mt-6 flex flex-wrap gap-3 lg:mt-0">
                                    <a
                                        href="/contactus"
                                        className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-300 transition hover:bg-slate-900"
                                    >
                                        Contact us
                                    </a>
                                    <a
                                        href="/"
                                        className="inline-flex items-center justify-center rounded-full border border-slate-950 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-yellow-300"
                                    >
                                        Explore services
                                    </a>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>

                    <Footer />
                </div>
            </div>
        </div>
    )
}

export default WebDevelopment